import { useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import getCMResponse from '../../utils/api_manager';
import styles from './AcceptInvite.module.css';

const INVITATION_EXPIRY_MS = 7 * 24 * 60 * 60 * 1000;

const ROLE_LABEL = {
  OWNER: 'Owner',
  ADMIN: 'Admin',
  ANALYST: 'Analyst',
  VIEWER: 'Viewer',
};

const AcceptInvite = ({ user, initUser, setCurrentProject }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const inviteId = searchParams.get('inviteId');
  const isLoggedIn = user && Object.keys(user).length > 0;

  const nonExpiredInvitations = useMemo(() => {
    const now = Date.now();
    return (user?.pending_invitations || []).filter(
      inv => (now - Number(inv.created_at)) < INVITATION_EXPIRY_MS
    );
  }, [user?.pending_invitations]);

  // LOGGED-OUT SCREEN
  if (!isLoggedIn) {
    return null;
  }

  // User is logged in
  if (inviteId) {
    const targetInvitation = nonExpiredInvitations.find(inv => inv.id === inviteId);

    // INVALID INVITE SCREEN
    if (!targetInvitation) {
      return (
        <div className={styles.pageBg}>
          <div className={styles.card}>
            <div className={styles.logoMark}>CQ</div>
            <h1 className={styles.title}>Invitation not found</h1>
            <p className={styles.subtitle}>
              This invitation is invalid, has already been used, or doesn't belong to your account.
            </p>
            <button
              className={styles.primaryButton}
              onClick={() => navigate('/create-project', { replace: true })}
            >
              Create a new project
            </button>
          </div>
        </div>
      );
    }

    // SPECIFIC INVITE SCREEN
    const handleSpecificAccept = () => {
      setLoading(true);
      setError(null);
      getCMResponse(`/project/invitation/${targetInvitation.id}/accept`, 'POST')
        .then(() => {
          initUser();
          // connect-cta-new-tab-project-leak-trd.md Section 3, R6: route
          // to the SPECIFIC project just joined (targetInvitation's own
          // project_id, already present on every pending_invitations
          // entry per docs/api/endpoints/user.md), not the legacy-alias
          // fallback's projects[0] -- which would be wrong whenever the
          // user already has other projects.
          navigate(`/${targetInvitation.project_id}/analyze/dashboards/all`, { replace: true });
        })
        .catch(() => {
          setError('Failed to accept the invitation. Please try again.');
          setLoading(false);
        });
    };

    const handleSpecificDecline = () => {
      setLoading(true);
      setError(null);
      getCMResponse(`/project/invitation/${targetInvitation.id}/reject`, 'POST')
        .then(() => {
          navigate('/create-project', { replace: true });
        })
        .catch(() => {
          setError('Failed to decline the invitation. Please try again.');
          setLoading(false);
        });
    };

    return (
      <div className={styles.pageBg}>
        <div className={styles.card}>
          <div className={styles.logoMark}>CQ</div>
          <h1 className={styles.title}>You've been invited</h1>
          <p className={styles.subtitle}>
            {targetInvitation.invited_by_name
              ? <><span className={styles.highlight}>{targetInvitation.invited_by_name}</span> has invited you to join</>
              : 'You have been invited to join'
            }
          </p>

          <div className={styles.projectBlock}>
            <span className={styles.projectName}>{targetInvitation.project_name || 'a project'}</span>
          </div>

          <div className={styles.roleBlock}>
            <span className={styles.roleLabel}>Your role</span>
            <span className={styles.roleBadge}>{ROLE_LABEL[targetInvitation.role] || targetInvitation.role}</span>
          </div>

          {error && <p className={styles.errorText}>{error}</p>}

          <div className={styles.ctaRow}>
            <button
              className={styles.rejectButton}
              onClick={handleSpecificDecline}
              disabled={loading}
            >
              Decline
            </button>
            <button
              className={styles.acceptButton}
              onClick={handleSpecificAccept}
              disabled={loading}
            >
              {loading ? 'Processing…' : 'Accept invitation'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // CAROUSEL SCREEN (no inviteId in URL — existing behaviour)
  const invitation = nonExpiredInvitations[currentIndex];

  if (!invitation) {
    return null;
  }

  const handleAccept = () => {
    setLoading(true);
    setError(null);
    getCMResponse(`/project/invitation/${invitation.id}/accept`, 'POST')
      .then(() => {
        initUser();
        // Same reasoning as handleSpecificAccept above -- route to this
        // specific invitation's project_id, not the legacy-alias
        // fallback's projects[0].
        navigate(`/${invitation.project_id}/analyze/dashboards/all`, { replace: true });
      })
      .catch(() => {
        setError('Failed to accept the invitation. Please try again.');
        setLoading(false);
      });
  };

  const handleReject = () => {
    setLoading(true);
    setError(null);
    getCMResponse(`/project/invitation/${invitation.id}/reject`, 'POST')
      .then(() => {
        const nextIndex = currentIndex + 1;
        if (nextIndex < nonExpiredInvitations.length) {
          setCurrentIndex(nextIndex);
          setLoading(false);
        } else {
          navigate('/create-project', { replace: true });
        }
      })
      .catch(() => {
        setError('Failed to decline the invitation. Please try again.');
        setLoading(false);
      });
  };

  return (
    <div className={styles.pageBg}>
      <div className={styles.card}>
        <div className={styles.logoMark}>CQ</div>
        <h1 className={styles.title}>You've been invited</h1>
        <p className={styles.subtitle}>
          {invitation.invited_by_name
            ? <><span className={styles.highlight}>{invitation.invited_by_name}</span> has invited you to join</>
            : 'You have been invited to join'
          }
        </p>

        <div className={styles.projectBlock}>
          <span className={styles.projectName}>{invitation.project_name || 'a project'}</span>
        </div>

        <div className={styles.roleBlock}>
          <span className={styles.roleLabel}>Your role</span>
          <span className={styles.roleBadge}>{ROLE_LABEL[invitation.role] || invitation.role}</span>
        </div>

        {nonExpiredInvitations.length > 1 && (
          <p className={styles.inviteCount}>
            Invitation {currentIndex + 1} of {nonExpiredInvitations.length}
          </p>
        )}

        {error && <p className={styles.errorText}>{error}</p>}

        <div className={styles.ctaRow}>
          <button
            className={styles.rejectButton}
            onClick={handleReject}
            disabled={loading}
          >
            Decline
          </button>
          <button
            className={styles.acceptButton}
            onClick={handleAccept}
            disabled={loading}
          >
            {loading ? 'Processing…' : 'Accept invitation'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AcceptInvite;
